import React, { useEffect, useState, useContext } from 'react';
import { fs } from '../../firebase';
import { getAuth } from 'firebase/auth';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { LocalizationContext } from '../../localization/LocalizationContext';
import { parseMultiLanguageDescription } from '../../utils/multiLanguageParser';
import { getKitchenStatus, getKitchenStatusStep } from '../../utils/kitchenStatus';
import { getMetaBySyrveKey } from '../../config/restaurantMeta';
import { formatOrderTypeForAdmin } from '../../utils/orderFulfillment';
import { useTranslation } from 'react-i18next';
import API_ENDPOINTS from '../../config/api';
import '../../css/OrderHistory.css';

const ACTIVE_STATUSES = ['pending', 'confirmed', 'processing'];

const formatOrderDate = (createdAt, t) => {
    if (!createdAt) return '—';
    const date = createdAt.toDate ? createdAt.toDate() : new Date(createdAt);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const orderDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());

    const timeStr = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    if (orderDay.getTime() === today.getTime()) {
        return `${t('Today')}, ${timeStr}`;
    }
    if (orderDay.getTime() === yesterday.getTime()) {
        return `${t('Yesterday')}, ${timeStr}`;
    }
    const monthDay = date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    return `${monthDay}, ${timeStr}`;
};

const getOrderRestaurantInfo = (order) => {
    const venueKeys = order.orderReceipt
        ? Object.keys(order.orderReceipt)
        : [...new Set((order.products || []).map(p => p.company).filter(Boolean))];

    const metas = venueKeys.map(key => getMetaBySyrveKey(key)).filter(Boolean);
    const names = metas.length > 0
        ? metas.map(m => m.name)
        : venueKeys;
    const logo = metas.length > 0 ? metas[0].logo : null;

    return {
        name: names.join(' & ') || 'Order',
        logo,
    };
};

const getItemCount = (order) => {
    if (!order.products) return 0;
    return order.products.reduce((sum, p) => sum + (p.quantity || 1), 0);
};

const getStatusConfig = (kitchenStatus) => {
    const configs = {
        pending: { className: 'fd-oh-status--pending', icon: '⏳' },
        confirmed: { className: 'fd-oh-status--confirmed', icon: '✓' },
        inKitchen: { className: 'fd-oh-status--preparing', icon: '🍳' },
        beingPrepared: { className: 'fd-oh-status--preparing', icon: '🍳' },
        ready: { className: 'fd-oh-status--on-the-way', icon: '✓' },
        onTheWay: { className: 'fd-oh-status--on-the-way', icon: '🚗' },
        completed: { className: 'fd-oh-status--delivered', icon: '✓' },
        failed: { className: 'fd-oh-status--cancelled', icon: '✕' },
        payment_failed: { className: 'fd-oh-status--payment-failed', icon: '⚠' },
    };
    return configs[kitchenStatus] || configs.pending;
};

const ProgressBar = ({ currentStep, t }) => {
    const steps = [
        { label: t('Cooking'), step: 1 },
        { label: t('On Delivery'), step: 2 },
        { label: t('Arrived'), step: 3 },
    ];

    return (
        <div className="fd-oh-progress">
            {steps.map((s, idx) => (
                <React.Fragment key={s.step}>
                    {idx > 0 && (
                        <div className={`fd-oh-progress-bar ${
                            currentStep > s.step - 1 ? 'fd-oh-progress-bar--done' :
                            currentStep === s.step - 1 ? 'fd-oh-progress-bar--active' : ''
                        }`} />
                    )}
                    <div className="fd-oh-progress-step">
                        <div className={`fd-oh-progress-dot ${
                            currentStep === s.step ? 'fd-oh-progress-dot--active' :
                            currentStep > s.step ? 'fd-oh-progress-dot--done' : ''
                        }`}>
                            {currentStep > s.step ? '✓' : s.step}
                        </div>
                        <span className={`fd-oh-progress-label ${
                            currentStep === s.step ? 'fd-oh-progress-label--active' :
                            currentStep > s.step ? 'fd-oh-progress-label--done' : ''
                        }`}>
                            {s.label}
                        </span>
                    </div>
                </React.Fragment>
            ))}
        </div>
    );
};

const OrderHistory = () => {
    const { translations, locale } = useContext(LocalizationContext);
    const { t } = useTranslation();
    const [orders, setOrders] = useState([]);
    const [activeTab, setActiveTab] = useState('active');
    const [expandedOrderId, setExpandedOrderId] = useState(null);
    const [productDetails, setProductDetails] = useState({});
    const [lastSyncTime, setLastSyncTime] = useState(null);
    const [autoRefreshEnabled] = useState(true);

    const auth = getAuth();
    const user = auth.currentUser;
    const companies = ["Aromiitaliani", "Poshnosh", "Copacabana", "Symposium", "Nadimi", "Cave"];

    const tl = translations?.deliveryPage;

    const getStatusLabel = (kitchenStatus) => {
        const labels = {
            'pending': t('Pending'),
            'confirmed': tl?.statusConfirmed || 'Confirmed',
            'completed': t('Delivered'),
            'failed': t('Cancelled'),
            'inKitchen': tl?.statusInKitchen || t('Preparing'),
            'beingPrepared': t('Preparing'),
            'ready': tl?.statusReady || 'Ready',
            'onTheWay': t('On the way'),
            'payment_failed': tl?.statusPaymentFailed || 'გადახდის პრობლემა',
        };
        return labels[kitchenStatus] || kitchenStatus;
    };

    const getSyrveStatusLabel = (syrveStatus) => {
        const map = {
            'Confirmed': tl?.statusConfirmed || 'Confirmed',
            'CookingStarted': t('Preparing'),
            'CookingCompleted': tl?.statusReady || 'Ready',
            'OnWay': t('On the way'),
            'Delivered': t('Delivered'),
            'Closed': t('Delivered'),
            'Cancelled': t('Cancelled'),
            'confirmed': tl?.statusConfirmed || 'Confirmed',
            'completed': t('Delivered'),
            'failed': t('Cancelled'),
            'pending': t('Pending'),
        };
        return map[syrveStatus] || syrveStatus;
    };

    const fetchOrders = async () => {
        try {
            const ordersQuery = query(
                collection(fs, "DeliveryOrder"),
                where("userId", "==", user.uid)
            );
            const querySnapshot = await getDocs(ordersQuery);
            const fetchedOrders = querySnapshot.docs
                .map(doc => ({ id: doc.id, ...doc.data() }))
                .sort((a, b) => b.createdAt.toDate() - a.createdAt.toDate());
            setOrders(fetchedOrders);
            return fetchedOrders;
        } catch (error) {
            console.error("Error loading orders:", error);
            return [];
        }
    };

    const syncOrderStatuses = async (showLoading = false) => {
        try {
            const response = await fetch(API_ENDPOINTS.syncOrderStatuses, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' }
            });
            const result = await response.json();
            console.log('🔄 [SYNC-RESULT]', result);
            if (result.success) {
                setLastSyncTime(new Date());
                await fetchOrders();
            }
        } catch (error) {
            console.error("Error syncing order statuses:", error);
        }
    };

    useEffect(() => {
        if (!user) return;
        fetchOrders().then(fetched => {
            const needsSync = fetched.some(order => ACTIVE_STATUSES.includes(order.status));
            if (needsSync) syncOrderStatuses();
        });
    }, [user]);

    useEffect(() => {
        if (!user || !autoRefreshEnabled) return;
        const hasActiveOrders = orders.some(order => ACTIVE_STATUSES.includes(order.status));
        if (!hasActiveOrders) return;

        const intervalId = setInterval(() => syncOrderStatuses(), 30000);
        return () => clearInterval(intervalId);
    }, [user, autoRefreshEnabled, orders]);

    const toggleDetails = async (orderId) => {
        if (expandedOrderId === orderId) {
            setExpandedOrderId(null);
            return;
        }
        const newProductDetails = {};
        for (const company of companies) {
            const snapshot = await getDocs(collection(fs, company));
            snapshot.forEach(doc => {
                newProductDetails[doc.id] = { id: doc.id, ...doc.data() };
            });
        }
        setProductDetails(newProductDetails);
        setExpandedOrderId(orderId);
    };

    const activeOrders = orders.filter(o => ACTIVE_STATUSES.includes(o.status));
    const pastOrders = orders.filter(o => !ACTIVE_STATUSES.includes(o.status));
    const recentPastOrders = pastOrders.slice(0, 3);
    const displayedOrders = activeTab === 'active' ? activeOrders : pastOrders;

    const renderOrderCard = (order) => {
        const kitchenStatus = getKitchenStatus(order);
        const step = getKitchenStatusStep(kitchenStatus);
        const statusConfig = getStatusConfig(kitchenStatus);
        const restaurantInfo = getOrderRestaurantInfo(order);
        const itemCount = getItemCount(order);
        const isActive = ACTIVE_STATUSES.includes(order.status);
        const isExpanded = expandedOrderId === order.id;

        return (
            <div key={order.id} className="fd-oh-card">
                <div className="fd-oh-card-header">
                    <div>
                        <div className="fd-oh-order-id">{t('Order')} #{order.id.slice(-6)}</div>
                        <div className="fd-oh-order-date">{formatOrderDate(order.createdAt, t)}</div>
                    </div>
                    <span className={`fd-oh-status ${statusConfig.className}`}>
                        <span className="fd-oh-status-icon">{statusConfig.icon}</span>
                        {getStatusLabel(kitchenStatus)}
                    </span>
                </div>

                <div className="fd-oh-restaurant">
                    {restaurantInfo.logo ? (
                        <img src={restaurantInfo.logo} alt={restaurantInfo.name} className="fd-oh-restaurant-logo" />
                    ) : (
                        <div className="fd-oh-restaurant-logo-placeholder">
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2" /><path d="M7 2v20" /><path d="M21 15V2v0a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3Zm0 0v7" />
                            </svg>
                        </div>
                    )}
                    <div className="fd-oh-restaurant-info">
                        <div className="fd-oh-restaurant-name">{restaurantInfo.name}</div>
                        <div className="fd-oh-restaurant-meta">
                            ₾{(order.totalPrice || 0).toFixed(2)} &bull; {itemCount} {itemCount === 1 ? t('item') : t('items')}
                        </div>
                    </div>
                </div>

                {isActive && step > 0 && order.status !== 'completed' && order.status !== 'failed' && (
                    <ProgressBar currentStep={step} t={t} />
                )}

                <div className="fd-oh-actions">
                    <button
                        className="fd-oh-btn fd-oh-btn--outline"
                        onClick={() => toggleDetails(order.id)}
                    >
                        {isExpanded ? t('Hide Details') : t('View Details')}
                    </button>
                    {isActive ? (
                        <button
                            className="fd-oh-btn fd-oh-btn--primary"
                            onClick={() => toggleDetails(order.id)}
                        >
                            {t('Track Order')}
                        </button>
                    ) : (
                        <button className="fd-oh-btn fd-oh-btn--reorder" onClick={() => {}}>
                            {t('Reorder')}
                        </button>
                    )}
                </div>

                {isExpanded && (
                    <div className="fd-oh-details">
                        <div className="fd-oh-order-info">
                            <div className="fd-oh-order-info-row">
                                <span className="fd-oh-order-info-label">{tl?.orderType || 'Type'}</span>
                                <span className="fd-oh-order-info-value">{formatOrderTypeForAdmin(order.orderType, translations)}</span>
                            </div>
                            {order.destination?.address && (
                                <div className="fd-oh-order-info-row">
                                    <span className="fd-oh-order-info-label">{tl?.address || 'Address'}</span>
                                    <span className="fd-oh-order-info-value">{order.destination.address}</span>
                                </div>
                            )}
                        </div>

                        {order.statusHistory && order.statusHistory.length > 0 && (
                            <div>
                                <h4 className="fd-oh-details-title">{tl?.statusHistory || 'Status History'}</h4>
                                {order.statusHistory.map((history, idx) => (
                                    <div key={idx} className="fd-oh-history-item">
                                        <span className="fd-oh-history-venue">
                                            {history.venue === 'all' ? t('Order') : history.venue}:
                                        </span>
                                        {' '}{getSyrveStatusLabel(history.previousStatus)}
                                        <span className="fd-oh-history-arrow">&rarr;</span>
                                        <span className="fd-oh-history-status">{getSyrveStatusLabel(history.status)}</span>
                                        <span className="fd-oh-history-time">
                                            {new Date(history.timestamp).toLocaleString()}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}

                        {order.products && order.products.length > 0 && (
                            <div>
                                <h4 className="fd-oh-details-title">{tl?.products || 'Products'}</h4>
                                {order.products.map((product, idx) => {
                                    const fullInfo = productDetails[product.productId];
                                    return (
                                        <div key={idx} className="fd-oh-product">
                                            {fullInfo?.imageUrl && (
                                                <img
                                                    src={fullInfo.imageUrl}
                                                    alt={product.name}
                                                    className="fd-oh-product-img"
                                                />
                                            )}
                                            <div className="fd-oh-product-info">
                                                <div className="fd-oh-product-name">
                                                    {parseMultiLanguageDescription(fullInfo?.title || product.name, locale) || product.name}
                                                </div>
                                                {fullInfo?.description && (
                                                    <div className="fd-oh-product-desc">
                                                        {parseMultiLanguageDescription(fullInfo.description, locale)}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="fd-oh-product-price">
                                                {product.quantity}x {product.price}₾
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="fd-oh">
            <div className="fd-oh-header">
                <h1 className="fd-oh-title">{t('My Orders History')}</h1>
                {lastSyncTime && activeOrders.length > 0 && (
                    <div className="fd-oh-sync-info">
                        {tl?.lastSynced || 'Last synced'}: {lastSyncTime.toLocaleTimeString()}
                    </div>
                )}
            </div>

            <div className="fd-oh-tabs">
                <button
                    className={`fd-oh-tab ${activeTab === 'active' ? 'fd-oh-tab--active' : ''}`}
                    onClick={() => setActiveTab('active')}
                >
                    {t('Active')} ({activeOrders.length})
                </button>
                <button
                    className={`fd-oh-tab ${activeTab === 'past' ? 'fd-oh-tab--active' : ''}`}
                    onClick={() => setActiveTab('past')}
                >
                    {t('Past')}
                </button>
            </div>

            {activeTab === 'active' ? (
                <>
                    {activeOrders.length === 0 && recentPastOrders.length === 0 ? (
                        <div className="fd-oh-empty">
                            <svg className="fd-oh-empty-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                <polyline points="14 2 14 8 20 8" />
                                <line x1="16" y1="13" x2="8" y2="13" />
                                <line x1="16" y1="17" x2="8" y2="17" />
                            </svg>
                            <h3 className="fd-oh-empty-title">{t('No orders yet')}</h3>
                            <p className="fd-oh-empty-subtitle">
                                {tl?.noActiveOrders || 'No active orders right now'}
                            </p>
                        </div>
                    ) : (
                        <>
                            {activeOrders.length > 0 && (
                                <>
                                    <div className="fd-oh-section-label">{t('Active')}</div>
                                    {activeOrders.map(order => renderOrderCard(order))}
                                </>
                            )}
                            {recentPastOrders.length > 0 && (
                                <>
                                    <div className="fd-oh-recently-completed">
                                        <div className="fd-oh-recently-completed-line" />
                                        <span className="fd-oh-recently-completed-text">{t('RECENTLY COMPLETED')}</span>
                                        <div className="fd-oh-recently-completed-line" />
                                    </div>
                                    {recentPastOrders.map(order => renderOrderCard(order))}
                                </>
                            )}
                        </>
                    )}
                </>
            ) : (
                <>
                    {pastOrders.length === 0 ? (
                        <div className="fd-oh-empty">
                            <svg className="fd-oh-empty-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                <polyline points="14 2 14 8 20 8" />
                                <line x1="16" y1="13" x2="8" y2="13" />
                                <line x1="16" y1="17" x2="8" y2="17" />
                            </svg>
                            <h3 className="fd-oh-empty-title">{t('No orders yet')}</h3>
                            <p className="fd-oh-empty-subtitle">
                                {tl?.noOrdersToDisplay || 'No past orders to display'}
                            </p>
                        </div>
                    ) : (
                        pastOrders.map(order => renderOrderCard(order))
                    )}
                </>
            )}
        </div>
    );
};

export default OrderHistory;
